export interface ShareData {
  listingId: string;
  title: string;
  price: number;
  location?: string;
  listingType: string;
  assetType?: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
}

export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

// Pretty share URLs on the brand domain. The /share/:type/:id route is a
// React page that renders the listing preview for humans. Social crawlers
// (WhatsApp, Telegram, LinkedIn, Facebook) read the static og:image from
// index.html — the branded ChopperSwap card (og-card.png).
// Host is resolved via shareHost.ts so admins can override it for testing.
import { getShareHost } from './shareHost';


export type ShareChannel =
  | 'whatsapp'
  | 'linkedin'
  | 'x'
  | 'facebook'
  | 'email'
  | 'copy'
  | 'native'
  | 'instagram';

export const generateShareUrl = (
  listingId: string,
  assetType: string = 'helicopter',
  channel: ShareChannel | 'social' = 'social',
): string => {
  const params = new URLSearchParams({
    utm_source: channel,
    utm_medium: 'social',
    utm_campaign: 'listing_share',
    utm_content: listingId,
  });
  if (assetType && assetType !== 'helicopter') {
    params.set('asset', assetType);
  }
  params.set('listing', listingId);
  return `${getShareHost()}/marketplace?${params.toString()}`;
};

export const buildRichCaption = (data: ShareData, url: string): string => {
  const text = generateShareText(data);
  const hashtags =
    data.assetType === 'jobs'
      ? '#AviationJobs #Helicopter #ChopperSwap'
      : data.assetType === 'services'
      ? '#AviationServices #Helicopter #ChopperSwap'
      : data.assetType === 'parts'
      ? '#AircraftParts #Helicopter #ChopperSwap'
      : data.assetType === 'engine'
      ? '#AircraftEngine #Helicopter #ChopperSwap'
      : '#Helicopter #Aviation #ChopperSwap';
  return `${text}\n\n${url}\n\n${hashtags}`;
};

export const generateShareText = (data: ShareData): string => {
  const { title, price, location, listingType, assetType } = data;
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(price);

  const locationText = location ? ` in ${location}` : '';

  // Asset-specific labels
  const assetLabel = 
    assetType === 'engine' ? 'engine' :
    assetType === 'parts' ? 'part/tool' :
    assetType === 'jobs' ? 'aviation job' :
    assetType === 'services' ? 'aviation service' :
    'helicopter';

  switch (listingType.toLowerCase()) {
    case 'sale':
    case 'sell':
    case 'engine_sell':
    case 'parts_sell':
      return `Check out this ${assetLabel} for sale: ${title} - ${formattedPrice}${locationText}`;
    case 'rent':
    case 'engine_rent':
    case 'parts_rent':
      return `Available for rent: ${title} - ${formattedPrice}/day${locationText}`;
    case 'lease':
    case 'dry_lease':
    case 'wet_lease':
      return `Helicopter lease opportunity: ${title} - ${formattedPrice}${locationText}`;
    case 'buy':
    case 'engine_buy':
    case 'parts_buy':
    case 'engine_rent_request':
    case 'parts_rent_request':
      return `Looking for: ${title} - Budget: ${formattedPrice}${locationText}`;
    case 'job_post':
    case 'job_contract':
      return `Aviation job opportunity: ${title}${formattedPrice !== '$0' ? ` - ${formattedPrice}` : ''}${locationText}`;
    case 'job_seeker':
      return `Aviation professional available: ${title}${locationText}`;
    case 'service_offer':
    case 'capability_listing':
      return `Aviation service provider: ${title}${locationText}`;
    case 'service_request':
    case 'rfq_request':
      return `Service request: ${title}${locationText}`;
    default:
      return `Check out this ${assetLabel}: ${title} - ${formattedPrice}${locationText}`;
  }
};

export const canUseNativeShare = (): boolean => {
  return typeof navigator !== 'undefined' && !!navigator.share && isMobileDevice();
};

export const getWhatsAppShareUrl = (text: string, url: string): string => {
  return `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`;
};

export const getEmailShareUrl = (subject: string, body: string, url: string): string => {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${body}\n\n${url}`)}`;
};

export const getTwitterShareUrl = (text: string, url: string): string => {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
};

export const getFacebookShareUrl = (url: string): string => {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
};

export const getLinkedInShareUrl = (url: string, title: string): string => {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
};

// Instagram doesn't have direct URL sharing - copying to clipboard is the only option
export const getInstagramShareText = (text: string, url: string): string => {
  return `${text}\n\n${url}\n\n#ChopperSwap #Aviation #Helicopter`;
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};
